/* eslint-disable no-underscore-dangle */
import * as React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { uniqueGUID } from "../../helpers/util";
import { AppContext, AppState } from "../Context/AppContext";

export enum NavigationEntryType {
  ROOT_PAGE = "root-page",
  REGULAR_PAGE = "regular-page",
  DETAILS_ROOT_PANEL = "details-root-panel",
  DETAILS_PANEL = "details-panel",
}

export interface NavigationEntry {
  type: NavigationEntryType;
  key: string;
  pageId?: string;
  pageLabel?: string;
  panelTitle?:string;
  panelPath?: string; // Used for details page panels
  path?: string; // used as fallback when the index of hisotry is -ve
}

interface NavigationHistoryEntry extends NavigationEntry {
  stamp: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  customData?: { [key:string]: any },
}

interface State {
  history: NavigationHistoryEntry[];
  registry: { [key:string]: NavigationEntry };
  breadcrumbLabelMap: { [key:string]: string };
  rootPageKey: string;
  currentEntryNdx: number;
}

const SessionStorageNavKey = "O4A-NAV";

class NavigationHistory {
  private _state: State = {
    history: [],
    registry: {},
    breadcrumbLabelMap: {},
    rootPageKey: "",
    currentEntryNdx: -1,
  };

  private _currentNdxListener!: (ndx: number) => void;

  constructor() {
    const navStored = window.sessionStorage.getItem(SessionStorageNavKey);
    if (navStored) {
      this._state = JSON.parse(navStored);
    }
  }

  public register = (
    key: string,
    type: NavigationEntryType,
    pageId: string,
    panelPath?: string,
    path?: string,
  ): void => {
    this._state.registry[key] = { key, type, pageId, panelPath, path } as NavigationEntry;
    if (type === NavigationEntryType.ROOT_PAGE) {
      this._state.rootPageKey = key;
    }
  };

  public regsiterBreadcrumbLabel = (key: string, label: string): void => {
    this._state.breadcrumbLabelMap[key] = label;
  };

  public push = (navigationEntry: NavigationHistoryEntry, resetPath = false): void => {
    if (resetPath || navigationEntry.type === NavigationEntryType.ROOT_PAGE) {
      if (!this._state.history.length) {
        this._state.history.push(navigationEntry);
      }
      // reset the index when user is coming from resetPath or home is clicked
      this.currentEntryNdx = 0;
    }

    this._state.history.splice(this.currentEntryNdx + 1);
    if (navigationEntry.type !== NavigationEntryType.ROOT_PAGE) {
      this._state.history.push(navigationEntry);
      this.currentEntryNdx++;
    }
  };

  public moveTo(key: string, stamp: string): void {
    const destNdx = this._state.history.findIndex(entry => entry.key === key && entry.stamp === stamp);
    if (destNdx >= 0) this.currentEntryNdx = destNdx;

    /*
    *  if key is root_page, reset the index. There might be some scenarios in which
    *  crossing boundaries will leave the index in wrong state. it's important.
    */
    const rootIndex = this._state.history.findIndex(
      entry => entry.key === key && entry.type === NavigationEntryType.ROOT_PAGE,
    );
    if (rootIndex >= 0) {
      this.currentEntryNdx = 0;
    }
  }

  public moveToRoot = (): void => {
    if (this.currentEntryNdx >= 0) this.currentEntryNdx = 0;
  };

  public back = (): number => {
    const currentEntry = this._state.history[this.currentEntryNdx];
    let nextPositionForCurrent = 0;
    for (let i = this.currentEntryNdx; i >= 0; i--) {
      if (this._state.history[i].pageId !== currentEntry.pageId) {
        nextPositionForCurrent = i;
        break;
      }
    }
    const noOfHopsToGoBack = this.currentEntryNdx - nextPositionForCurrent;
    this.currentEntryNdx = nextPositionForCurrent < 0 ? 0 : nextPositionForCurrent;
    return noOfHopsToGoBack < 0 ? 0 : noOfHopsToGoBack;
  };

  public backTo = (index: number): number => {
    let tempIndex = index;
    const currentEntry = this._state.history[index];
    if (currentEntry?.type === NavigationEntryType.DETAILS_PANEL) {
      for (let i = index; i >= 0; i--) {
        if (this._state.history[i]?.pageId !== currentEntry?.pageId) {
          tempIndex = i + 1;
          break;
        }
      }
    }
    const backCount = this.currentEntryNdx - tempIndex;
    this.currentEntryNdx = tempIndex;
    return backCount;
  };

  public backToPipe = (index: number): number => {
    const backCount = this.currentEntryNdx - index;
    this.currentEntryNdx = index;
    return backCount;
  };

  public get rootPageKey(): string { return this._state.rootPageKey; }

  public get history(): NavigationHistoryEntry[] { return this._state.history; }

  public get registry(): { [key:string]: NavigationEntry } { return this._state.registry; }

  public get breadcrumbLabelMap(): { [key:string]: string } { return this._state.breadcrumbLabelMap; }

  public get currentEntryNdx(): number { return this._state.currentEntryNdx; }

  public set currentEntryNdx(ndx: number) {
    this._state.currentEntryNdx = ndx;
    this._currentNdxListener?.(this._state.currentEntryNdx);
  }

  public addCurrentNdxListener = (listener: (ndx: number) => void): void => {
    this._currentNdxListener = listener;
  };
}

const navigationHistory = new Proxy(new NavigationHistory(), {
  get: (target, prop) => {
    const targetProp = prop in target ? target[prop as keyof typeof target] : undefined;
    if (typeof targetProp === "function" && prop !== "addCurrentNdxListener") {
      return new Proxy(targetProp, {
        apply: (funcTarget, thisArg, argumentsList) => {
          const result = Reflect.apply(funcTarget, thisArg, argumentsList);
          window.sessionStorage.setItem(SessionStorageNavKey, JSON.stringify(Reflect.get(target, "_state")));
          return result;
        },
      });
    }
    return Reflect.get(target, prop);
  },
  set: (obj, prop, value): boolean => {
    const success = Reflect.set(obj, prop, value);
    window.sessionStorage.setItem(SessionStorageNavKey, JSON.stringify(Reflect.get(obj, "_state")));
    return success;
  },
});

export const clearNavHistoryStorage = (): void => {
  window.sessionStorage.removeItem(SessionStorageNavKey);
};

export const registerNavigationEntry = (
  key: string,
  type: NavigationEntryType,
  pageId: string,
  panelPath?: string,
  path?: string,
): void => {
  navigationHistory.register(key, type, pageId, panelPath, path);
};

export const regsiterBreadcrumbLabel = (key: string, label: string): void => {
  navigationHistory.regsiterBreadcrumbLabel(key, label);
};

export type Back = () => void;
export type NavigateToSelf = (path: string, key: string) => void;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type NavigateTo = (path: string, key: string, customData?: any, resetPath?: boolean) => void;
export type NavigateToPanel = (key: string) => void;

export type NavigationHandlers = {
  navigateToSelf: NavigateToSelf;
  navigateTo: NavigateTo;
  navigateToPanel: NavigateToPanel;
  back: Back;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  customData: any;
  setPageDetails: (title:string, suffix:string) => void;
};

export function useNavigation<T extends AppState>(appContext: AppContext<T>): NavigationHandlers {
  const appState = React.useContext<T>(appContext);
  const navigate = useNavigate();
  const location = useLocation();

  const navigationState = location.state as NavigationHistoryEntry;
  if (!navigationState) {
    navigationHistory.moveToRoot();
  } else {
    navigationHistory.moveTo(navigationState.key, navigationState.stamp);
  }

  const navigateToSelf: NavigateToSelf = (path: string, key: string): void => {
    const selfNdx = navigationHistory.history.findIndex(entry => entry.key === key);
    if (selfNdx < 0) {
      let replace = true;
      const navigationEntry = navigationHistory.registry[key];
      if (navigationEntry) {
        if (navigationEntry.type !== NavigationEntryType.ROOT_PAGE) {
          const rootNavigationEntry = {
            type: NavigationEntryType.ROOT_PAGE,
            key: appState.homeKey,
            stamp: uniqueGUID(),
          } as NavigationHistoryEntry;
          navigationHistory.push(rootNavigationEntry);
          navigate(appState.homePath || "/", { state: rootNavigationEntry, replace });
          replace = false;
        }

        const navigationHistoryEntry = { ...navigationEntry, ...{ stamp: uniqueGUID() } };
        navigationHistory.push(navigationHistoryEntry);
        navigate(path, { state: navigationHistoryEntry, replace });
      }
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const navigateTo: NavigateTo = (path: string, key: string, customData?: any, resetPath = false): void => {
    const navigationEntry = navigationHistory.registry[key];
    if (navigationEntry) {
      const navigationHistoryEntry = { ...navigationEntry, stamp: uniqueGUID(), customData };
      /**
       * If the resetPath is true, check if index >= 0, it means we have to go back in history and clear it
       * before navigating to destination. if the index is equal to zero or destination is root, do nothing.
       * It means we are already at root.
       */
      if (resetPath && navigationHistory.currentEntryNdx >= 0) {
        if (navigationEntry.type === NavigationEntryType.ROOT_PAGE && navigationHistory.currentEntryNdx === 0) {
          // do nothing ...
        } else {
          if (navigationHistory.currentEntryNdx > 0) {
            navigate(-navigationHistory.currentEntryNdx);
          }
          setTimeout(() => {
            if (navigationEntry.type !== NavigationEntryType.ROOT_PAGE) {
              navigationHistory.push(navigationHistoryEntry, resetPath);
              navigate(path, { state: navigationHistoryEntry });
            }
          }, 1);
        }
      } else {
        navigationHistory.push(navigationHistoryEntry, resetPath);
        navigate(path, { state: navigationHistoryEntry });
      }
    }
  };

  const navigateToPanel: NavigateToPanel = (key: string): void => {
    const navigationEntry = navigationHistory.registry[key];
    const pathTokens = location.pathname.split("/");

    const panelName = pathTokens.pop();

    if (panelName === navigationEntry?.panelPath) return;

    const panelUrl = `${pathTokens.join("/")}/${navigationEntry?.panelPath}${location.search || ""}`;
    navigateTo(panelUrl, key);
  };

  const back: Back = (): void => {
    const viewIndex = navigationHistory.back();
    if (viewIndex >= 0) {
      navigate(viewIndex === 0 ? -1 : -viewIndex);
    } else {
      const currentIndex = navigationHistory.currentEntryNdx;
      const historyEntry = navigationHistory.history[currentIndex <= 0 ? 0 : currentIndex];
      const path = navigationHistory.registry[historyEntry.key]?.path || "/";
      navigate(path, { state: historyEntry });
    }
  };

  const customData = navigationHistory.history[navigationHistory.currentEntryNdx]?.customData;

  const setPageDetails = (title:string, suffix:string): void => {
    if (navigationHistory.currentEntryNdx >= 0) {
      if (navigationHistory.currentEntryNdx === 0) {
        navigationHistory.history[navigationHistory.currentEntryNdx].pageLabel = undefined;
      } else {
        navigationHistory.history[navigationHistory.currentEntryNdx].pageLabel = title;
        navigationHistory.history[navigationHistory.currentEntryNdx].panelTitle = suffix;
      }
    }
  };

  return { navigateToSelf, navigateTo, navigateToPanel, back, customData, setPageDetails };
}

export interface BreadcrumbItem {
  key: string;
  label: string;
  hideDivider?: boolean;
}

export interface BreadcrumbsHandlers {
  breadcrumbs: BreadcrumbItem[];
  backTo: (index: number) => void;
  backToPipe: (index: number) => void;
}

export const useBreadcrumbs = (): BreadcrumbsHandlers => {
  const navigate = useNavigate();

  const activeHistory = navigationHistory.history.slice(0, navigationHistory.currentEntryNdx + 1);
  let lastEntry = activeHistory[activeHistory.length - 1];
  const breadcrumbs: BreadcrumbItem[] = [];
  for (let i = activeHistory.length - 1; i >= 0; i--) {
    const label = activeHistory[i]?.pageLabel || navigationHistory.breadcrumbLabelMap[activeHistory[i].key];
    if (activeHistory[i]?.pageId !== lastEntry.pageId) {
      if (activeHistory[i]?.type === NavigationEntryType.DETAILS_PANEL) {
        const panelForPipe = activeHistory[i]?.panelTitle || activeHistory[i]?.pageLabel;
        const labelWithPipe = `${label} | ${panelForPipe}`;
        breadcrumbs.push({ key: `${activeHistory[i].key}$${i}#${i}`, label: labelWithPipe });
      } else {
        breadcrumbs.push({ key: `${activeHistory[i].key}$${i}`, label });
      }
    }
    lastEntry = activeHistory[i];
  }

  if (activeHistory[activeHistory.length - 1]?.type === NavigationEntryType.DETAILS_PANEL) {
    const entry = activeHistory[activeHistory.length - 1];
    const label = entry?.pageLabel || navigationHistory.breadcrumbLabelMap[entry.key];
    breadcrumbs.unshift({ key: `${entry.key}$${activeHistory.length - 1}`, label });
  }
  breadcrumbs.reverse();
  breadcrumbs.push({ key: "O4A-BC-END", label: "" });

  const backTo = (index: number): void => {
    const viewIndex = navigationHistory.backTo(index);
    if (viewIndex >= 0) {
      navigate(viewIndex === 0 ? -1 : -viewIndex);
    } else {
      const currentIndex = navigationHistory.currentEntryNdx;
      const historyEntry = navigationHistory.history[currentIndex <= 0 ? 0 : currentIndex];
      const path = navigationHistory.registry[historyEntry.key]?.path || "/";
      navigate(path, { state: historyEntry });
    }
  };

  const backToPipe = (index: number): void => {
    const viewIndex = navigationHistory.backToPipe(index);
    if (viewIndex >= 0) {
      navigate(viewIndex === 0 ? -1 : -viewIndex);
    } else {
      const currentIndex = navigationHistory.currentEntryNdx;
      const historyEntry = navigationHistory.history[currentIndex <= 0 ? 0 : currentIndex];
      const path = navigationHistory.registry[historyEntry.key]?.path || "/";
      navigate(path, { state: historyEntry });
    }
  };

  return { breadcrumbs, backTo, backToPipe };
};

export interface NavigationLinkProps {
  to: string;
  pageKey: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  customData?: { [key:string]: any },
  children: React.ReactNode
}

export const NavigationLink = ({ to, pageKey, customData, children }: NavigationLinkProps): JSX.Element => {
  const navigationEntry = React.useMemo(() => {
    const entry = navigationHistory.registry[pageKey];
    return entry ? { ...entry, stamp: uniqueGUID(), customData }
      : {} as NavigationHistoryEntry;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [to, pageKey, customData]);

  const onClick = (): void => {
    navigationHistory.push(navigationEntry);
  };

  return (
    <Link
      to={to}
      state={navigationEntry}
      onClick={onClick}
    >
      {children}
    </Link>
  );
};
